/**
 * Syncs PDP price when Recharge subscription widget switches
 * between one-time and subscription (or between selling plans).
 *
 * Recharge widget v2 does not expose purchase-option change events,
 * so we observe the form's selling_plan input and widget interaction,
 * then dispatch custom events and refresh #Product-Price-*.
 */
(() => {
  const CUSTOM_EVENT = 'recharge:purchase-option-change';
  const WIDGET_SELECTOR = 'recharge-subscription-widget';
  const SYNC_DELAY_MS = 75;

  let lastSellingPlanId = null;
  let syncTimer = null;
  let bound = false;

  const getMainProduct = () =>
    document.querySelector('[data-main-product]');

  const getSectionId = () => getMainProduct()?.dataset?.section;

  const getProductForm = sectionId => {
    if (sectionId) {
      const byId = document.querySelector(`#ProductForm-${sectionId} form`);
      if (byId) return byId;
    }

    return (
      document.querySelector('product-form form') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  };

  const getProductSelector = sectionId => {
    if (sectionId) {
      return document.querySelector(
        `product-selector[data-section-id="${sectionId}"]`
      );
    }

    return document.querySelector('product-selector');
  };

  const getSellingPlanId = form => {
    const input = form?.querySelector('[name="selling_plan"]');
    if (!input) return null;

    const value = input.value?.trim();
    return value || null;
  };

  const getVariantId = (form, productSelector) => {
    if (productSelector?.currentVariant?.id) {
      return String(productSelector.currentVariant.id);
    }

    const input =
      form?.querySelector('[name="id"]') ||
      document.querySelector(`#ProductForm-${getSectionId()} [name="id"]`) ||
      document.querySelector('[name="id"]');

    return input?.value ? String(input.value) : null;
  };

  const getProductUrl = productSelector => {
    if (productSelector?.dataset?.url) return productSelector.dataset.url;

    const handle = getMainProduct()?.dataset?.productHandle;
    return handle ? `/products/${handle}` : window.location.pathname;
  };

  const updateProductFormState = (productForm, sellingPlanId) => {
    if (!productForm) return;

    if (sellingPlanId) {
      productForm.setAttribute('data-has-selling-plan', '');
    } else {
      productForm.removeAttribute('data-has-selling-plan');
    }
  };

  const updateUrl = (variantId, sellingPlanId) => {
    const params = new URLSearchParams(window.location.search);

    if (variantId) {
      params.set('variant', variantId);
    }

    if (sellingPlanId) {
      params.set('selling_plan', sellingPlanId);
    } else {
      params.delete('selling_plan');
    }

    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      query ? `${window.location.pathname}?${query}` : window.location.pathname
    );
  };

  const replacePriceHtml = (sectionId, html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const selectors = [
      `#Product-Price-${sectionId}`,
      `#Product-Purchase-Options-${sectionId}`
    ];

    selectors.forEach(selector => {
      const destination = document.querySelector(selector);
      const source = doc.querySelector(selector);
      if (destination && source) {
        destination.classList.remove('visibility-hidden');
        destination.innerHTML = source.innerHTML;
      }
    });
  };

  const fetchAndUpdatePrice = ({
    productUrl,
    sectionId,
    variantId,
    sellingPlanId
  }) => {
    const params = new URLSearchParams(window.location.search);
    if (variantId) params.set('variant', variantId);
    if (sellingPlanId) {
      params.set('selling_plan', sellingPlanId);
    } else {
      params.delete('selling_plan');
    }
    params.set('section_id', sectionId);

    return fetch(`${productUrl}?${params.toString()}`)
      .then(response => response.text())
      .then(html => replacePriceHtml(sectionId, html));
  };

  const dispatchPurchaseOptionChange = detail => {
    document.dispatchEvent(
      new CustomEvent(CUSTOM_EVENT, {
        bubbles: true,
        detail
      })
    );
  };

  const syncPrice = ({ force = false } = {}) => {
    const sectionId = getSectionId();
    const form = getProductForm(sectionId);
    if (!sectionId || !form) return;

    const productSelector = getProductSelector(sectionId);
    const sellingPlanId = getSellingPlanId(form);
    const variantId = getVariantId(form, productSelector);

    if (!force && sellingPlanId === lastSellingPlanId) return;

    lastSellingPlanId = sellingPlanId;

    const detail = {
      sellingPlanId,
      variantId,
      isSubscription: Boolean(sellingPlanId),
      sectionId
    };

    dispatchPurchaseOptionChange(detail);

    const productForm = document.querySelector(`#ProductForm-${sectionId}`);
    updateProductFormState(productForm, sellingPlanId);

    if (
      productSelector &&
      typeof productSelector.updateURL === 'function' &&
      typeof productSelector.renderProductInfo === 'function' &&
      productSelector.currentVariant
    ) {
      productSelector.updateURL(sellingPlanId);
      productSelector.renderProductInfo();
      return;
    }

    updateUrl(variantId, sellingPlanId);
    fetchAndUpdatePrice({
      productUrl: getProductUrl(productSelector),
      sectionId,
      variantId,
      sellingPlanId
    });
  };

  const scheduleSync = (options = {}) => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => syncPrice(options), SYNC_DELAY_MS);
  };

  const isRechargeTarget = event => {
    const path =
      typeof event.composedPath === 'function' ? event.composedPath() : [];

    return path.some(node => {
      if (!node || node === document || node === window) return false;
      if (node.matches?.(WIDGET_SELECTOR)) return true;
      if (node.tagName === 'RECHARGE-SUBSCRIPTION-WIDGET') return true;
      if (node.getRootNode?.() instanceof ShadowRoot) {
        const host = node.getRootNode().host;
        return host?.matches?.(WIDGET_SELECTOR);
      }
      return false;
    });
  };

  const onDocumentInteraction = event => {
    if (!isRechargeTarget(event)) return;
    scheduleSync();
  };

  const onFormChange = event => {
    if (event.target?.name === 'selling_plan') {
      scheduleSync({ force: true });
    }
  };

  const observeSellingPlanInput = form => {
    const attachInputListener = input => {
      if (!input || input.dataset.rechargePriceSyncBound) return;
      input.dataset.rechargePriceSyncBound = 'true';
      input.addEventListener('change', () => scheduleSync({ force: true }));
    };

    attachInputListener(form.querySelector('[name="selling_plan"]'));

    const observer = new MutationObserver(mutations => {
      let shouldSync = false;

      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          if (node.matches?.('[name="selling_plan"]')) {
            attachInputListener(node);
            shouldSync = true;
          }

          node
            .querySelectorAll?.('[name="selling_plan"]')
            .forEach(input => {
              attachInputListener(input);
              shouldSync = true;
            });
        });

        if (
          mutation.type === 'attributes' &&
          mutation.target?.name === 'selling_plan'
        ) {
          shouldSync = true;
        }
      });

      if (shouldSync) scheduleSync();
    });

    observer.observe(form, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value']
    });
  };

  const waitForWidget = callback => {
    if (document.querySelector(WIDGET_SELECTOR)) {
      callback();
      return;
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(WIDGET_SELECTOR)) {
        observer.disconnect();
        callback();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const init = () => {
    if (bound) return;

    const sectionId = getSectionId();
    const form = getProductForm(sectionId);
    if (!form) return;

    bound = true;
    lastSellingPlanId = getSellingPlanId(form);

    form.addEventListener('change', onFormChange);
    document.addEventListener('click', onDocumentInteraction, true);
    document.addEventListener('change', onDocumentInteraction, true);

    observeSellingPlanInput(form);

    waitForWidget(() => {
      scheduleSync({ force: true });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
